import styled, { css } from 'styled-components';
import { Picture } from '../../fuse-picture/src/picture';
import { gridConfig } from '../../fuse-responsive-grid/src/grid';
import { TypographyComponent } from '../../fuse-typography-element/src/typography';
import { styledComponentConfig, variants } from './image-hero-2-config';

const { contentWrapper, imageHero, imageWrapper } = styledComponentConfig;

const spanButtonHoverCss = css`
	box-shadow: var(--elevation-shadow-xxs);
	background-color: var(--color-brand-mid);

	@media screen and (min-width: ${gridConfig.m.minWidth}) {
		box-shadow: var(--elevation-shadow-xs);
	}

	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		box-shadow: var(--elevation-shadow-s);
	}
`;

const StyledImageHero = styled.div.attrs({
	className: 'fuse-image-hero-2',
})`
	${({ background }) => `
		background-color: ${imageHero.backgroundColor[background]};
		border: ${imageHero.border[background]};
		position: relative;
		text-decoration: none;

		&:hover .fuse-image-hero-2__span-button {
			${spanButtonHoverCss}
		}

		&:focus-visible .fuse-image-hero-2__span-button {
			${spanButtonHoverCss}
		}

		&:focus-visible {
			outline: var(--border-radius-s) solid var(--color-functional-focus);
		}

		&:active .fuse-image-hero-2__span-button {
			box-shadow: inset 0 0 0 2px var(--color-global-white);
			color: var(--color-global-white);
			background-color: var(--color-brand-dark);
		}
	`}
`;

const StyledImageHeroContainer = styled.div.attrs({
	className: 'fuse-image-hero-2__container',
	'data-testid': 'fuse-image-hero-2-container-test-id',
})`
	display: flex;
	flex-direction: column;

	@media screen and (min-width: ${gridConfig.m.minWidth}) {
		flex-direction: ${({ imagePosition }) => (imagePosition === 'right' ? `row-reverse` : `row`)};
	}
`;

const StyledImageWrapper = styled.div
	.withConfig({ shouldForwardProp: (prop) => prop !== 'imageRatio' })
	.attrs({
		className: 'fuse-image-hero-2__image-wrapper',
		'data-testid': 'fuse-image-hero-2__image-wrapper',
	})`
	${({ variant, imageRatio }) => `
		aspect-ratio: ${variant === 'v2' ? '4/3' : 'auto'};
		align-items: ${imageWrapper.alignItems[variant]};
		justify-content: flex-end;
		display: ${imageWrapper.displayMobile[variant]};
		width: ${imageWrapper.width.xxs[variant]};

		@media screen and (min-width: ${gridConfig.m.minWidth}) {
			aspect-ratio: ${variant === 'v2' && imageRatio === '1:1' ? '1/1' : '4/3'};
			align-items: center;
			display: flex;
			flex: 0 0 ${
	variant === 'v2' ? imageWrapper.width.m[variant][imageRatio] : imageWrapper.width.m[variant]
};
		}

		@media screen and (min-width: ${gridConfig.l.minWidth}) {
			flex: 0 0 ${
	variant === 'v2' ? imageWrapper.width.l[variant][imageRatio] : imageWrapper.width.l[variant]
};
			min-width: 334px;
		}

	`}
`;

const StyledPicture = styled(Picture)
	.withConfig({ shouldForwardProp: (prop) => prop !== 'imageRatio' })
	.attrs({
		className: 'fuse-image-hero-2__picture',
		'data-testid': 'fuse-image-hero-2-picture-test-id',
	})`
	.fuse-picture__img {
		aspect-ratio: 4/3;
		display: block;
		max-width: none;
		object-fit: cover;
		width: 100%;
	}

	${({ variant, imageRatio }) =>
		variant === variants.v3
			? `
				@media screen and (max-width: ${gridConfig.s.maxWidth}) {
					flex: 0 0 50%;
					max-width: 50%;
				}

			`
			: `
				@media screen and (min-width: ${gridConfig.m.minWidth}) {
					.fuse-picture__img {
						aspect-ratio: ${imageRatio === '1:1' ? '1/1' : '4/3'};
					}
				}
			`}
`;

const StyledImageHeading = styled(TypographyComponent).attrs({
	className: 'fuse-image-hero-2__image-heading',
	'data-testid': 'fuse-image-hero-2-image-heading-test-id',
})`
	flex: 0 0 50%;
	padding: var(--spacing-global-space3);
	max-width: 50%;
	width: fit-content;

	@media screen and (min-width: ${gridConfig.m.minWidth}) {
		display: none;
	}
`;

const StyledContentWrapper = styled.div.attrs({
	className: 'fuse-image-hero-2__content-wrapper',
	'data-testid': 'fuse-image-hero-2__content-wrapper',
})`
	${({ imagePosition, noMobileContentPadding, variant }) => `
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		justify-content: center;
		gap: ${contentWrapper.gap.xxs[variant]};
		padding: ${
	noMobileContentPadding ? '0' : contentWrapper.padding.xxs[variant]
}; /* No padding when all text and buttons are absent apart from headline for mobile */
		z-index: 1;

		@media screen and (min-width: ${gridConfig.m.minWidth}) {
			gap: ${contentWrapper.gap.m[variant]};
			padding: ${contentWrapper.padding.m[variant][imagePosition]};
		}
		@media screen and (min-width: ${gridConfig.l.minWidth}) {
			gap: ${contentWrapper.gap.l[variant]};
			padding: ${contentWrapper.padding.l[variant]};
		}
	`};
`;

const StyledCopyWrapper = styled.div.attrs({
	className: 'fuse-image-hero-2__text-content',
	'data-testid': 'fuse-image-hero-2__text-content',
})`
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-global-space3);
	justify-content: center;
`;

const StyledHeading = styled(TypographyComponent).attrs({
	className: 'fuse-image-hero-2__heading',
	'data-testid': 'fuse-image-hero-2__heading',
})`
	width: fit-content;

	@media screen and (min-width: ${gridConfig.m.minWidth}) {
		gap: var(--spacing-global-space6);
	}

	@media screen and (max-width: ${gridConfig.s.maxWidth}) {
		display: ${({ variant }) => (variant === variants.v3 ? 'none' : 'block')};
	}
`;

const StyledSubheading = styled(TypographyComponent).attrs({
	className: 'fuse-image-hero-2__subheadline',
	'data-testid': 'fuse-image-hero-2__subheadline',
})``;

const StyledDescription = styled(TypographyComponent).attrs({
	className: 'fuse-image-hero-2__description',
	'data-testid': 'fuse-image-hero-2__description',
})`
	margin-bottom: 0;
	
	strong {
		font-weight: var(--font-weight-heading);
	}
		
	@media screen and (min-width: ${gridConfig.m.minWidth}) {
        padding-right: var(--spacing-global-space4);
    }
`;

const backgroundLightRep = (prop) =>
	prop.background === 'brandLight' ? 'var(--color-brand-dark)' : 'var(--color-brand-mid)';

const StyledRepresentative = styled(TypographyComponent)
	.withConfig({
		shouldForwardProp: (prop) => prop !== 'representativeColour',
	})
	.attrs({
		className: 'fuse-image-hero-2__representative',
		'data-testid': 'fuse-image-hero-2__representative',
	})`
	color: ${({ representativeColour }) =>
		representativeColour === 'grey' ? 'var(--color-global-grey900)' : backgroundLightRep};

	span.fuse-image-hero-2__wrap-apr {
	display: inline-flex;
	}
`;

const StyledTerms = styled(TypographyComponent).attrs({
	className: 'fuse-image-hero-2__terms',
	'data-testid': 'fuse-image-hero-2__terms',
})`
	margin-bottom: 0;

	strong {
		font-weight: var(--font-weight-heading);
	}
`;

const StyledButtons = styled.div.attrs({
	className: 'fuse-image-hero-2__buttons',
	'data-testid': 'fuse-image-hero-2-buttons-test-id',
})`
	display: flex;
	flex-wrap: wrap;
	gap: var(--spacing-global-space5);

	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		gap: var(--spacing-global-space6);
	}

	&.fuse-image-hero-2__buttons--stacked {
		flex-direction: column;
		gap: var(--spacing-global-space4);
		width: fit-content;
	}

	.fuse-image-hero-2__primary-button--stacked,
	.fuse-image-hero-2__secondary-button--stacked {
		width: 100%;
	}
`;

export {
	StyledCopyWrapper,
	StyledContentWrapper,
	StyledSubheading,
	StyledDescription,
	StyledRepresentative,
	StyledHeading,
	StyledImageHeading,
	StyledImageHero,
	StyledImageHeroContainer,
	StyledImageWrapper,
	StyledPicture,
	StyledTerms,
	StyledButtons,
};
